import { z } from 'zod';

import {
  ActionRequestStateSchema,
  ActorTypeSchema,
  AlertActionSchema,
  AlertStateSchema,
  AlertViewSchema,
  ChangeOrderStatusSchema,
  DetectionSchema,
  DiagnosisSchema,
  RecommendationKindSchema,
  SeveritySchema,
} from '../enums.js';

import { ExpectedVersionSchema, IsoDateSchema, IsoDateTimeSchema, UuidSchema } from './common.js';

// alert-service: alerts list, detail, context, actions and bulk accept (plan Phase 7.4).

/** Label and pill for the state, from `ui/labels.ts`, so the UI never maps states itself. */
export const AlertUiSchema = z.object({
  label: z.string(),
  pillClass: z.string(),
  statusKey: z.string(),
});

export const AlertsQuerySchema = z.object({
  view: AlertViewSchema.default('all'),
  severity: z.coerce.number().pipe(SeveritySchema).optional(),
  assemblyId: UuidSchema.optional(),
  state: AlertStateSchema.optional(),
});
export type AlertsQuery = z.infer<typeof AlertsQuerySchema>;

export const PurchaseOrderRefSchema = z.object({ code: z.string(), releaseDate: IsoDateSchema });
export const ChangeOrderRefSchema = z.object({
  code: z.string(),
  status: ChangeOrderStatusSchema,
});

export const AlertListItemSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  title: z.string(),
  summary: z.string(),
  partPn: z.string(),
  partDescription: z.string(),
  assemblyId: UuidSchema,
  assemblyName: z.string(),
  severity: SeveritySchema,
  state: AlertStateSchema,
  ui: AlertUiSchema,
  openedAt: IsoDateTimeSchema,
  purchaseOrder: PurchaseOrderRefSchema.nullable(),
  version: z.number().int().nonnegative(),
});
export type AlertListItem = z.infer<typeof AlertListItemSchema>;

export const AlertsResponseSchema = z.object({
  alerts: z.array(AlertListItemSchema),
  /** Count per view under the same severity/assembly filters, for "View: … (n)". */
  counts: z.record(AlertViewSchema, z.number().int().nonnegative()),
});
export type AlertsResponse = z.infer<typeof AlertsResponseSchema>;

export const RecommendationSchema = z.object({
  kind: RecommendationKindSchema,
  label: z.string(),
  text: z.string(),
  /** BR-004: plain-language rationale citing the rules and evidence. */
  rationale: z.string(),
});
export type Recommendation = z.infer<typeof RecommendationSchema>;

/** BR-005: severity is the output of named rules, e.g. `SEV-BASE-QTY`, `SEV-UP-PO7`. */
export const SeverityRuleSchema = z.object({
  ruleId: z.string(),
  effect: z.enum(['base', 'uplift', 'cap']),
  text: z.string(),
});
export type SeverityRule = z.infer<typeof SeverityRuleSchema>;

export const TimelineEntrySchema = z.object({
  id: UuidSchema,
  at: IsoDateTimeSchema,
  actorType: ActorTypeSchema,
  actorLabel: z.string(),
  /** "B" for the agent, the user's initials, or "•" for others (prototype timeline). */
  avatar: z.string(),
  type: z.string(),
  text: z.string(),
  rationale: z.string().nullable(),
});
export type TimelineEntry = z.infer<typeof TimelineEntrySchema>;

export const AlertDetailSchema = AlertListItemSchema.extend({
  assemblyPn: z.string(),
  configuration: z.string(),
  parentPath: z.string(),
  detection: DetectionSchema,
  diagnosis: DiagnosisSchema,
  diagnosisLabel: z.string(),
  pdmQty: z.number().nullable(),
  erpQty: z.number().nullable(),
  pdmRev: z.string().nullable(),
  erpRev: z.string().nullable(),
  changeOrder: ChangeOrderRefSchema.nullable(),
  owner: z.object({ userId: UuidSchema.nullable(), name: z.string(), role: z.string() }),
  recommendation: RecommendationSchema,
  severityRules: z.array(SeverityRuleSchema),
  timeline: z.array(TimelineEntrySchema),
  /** Spec §4 "not yet reflected" marker while inside the verification grace period. */
  notYetReflected: z.boolean(),
  draftId: UuidSchema.nullable(),
  openRequest: z
    .object({
      id: UuidSchema,
      requestedName: z.string(),
      requestedRole: z.string(),
      state: ActionRequestStateSchema,
    })
    .nullable(),
  /** Actions the caller may take now; the UI shows only these (it holds no rules). */
  allowedActions: z.array(AlertActionSchema),
  /** Position within the caller's current filter, for the "i of n" stepper. */
  position: z.object({
    index: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    prevId: UuidSchema.nullable(),
    nextId: UuidSchema.nullable(),
  }),
  closedReason: z.string().nullable(),
  deviationReason: z.string().nullable(),
  closedAt: IsoDateTimeSchema.nullable(),
});
export type AlertDetail = z.infer<typeof AlertDetailSchema>;

export const AlertIdParamsSchema = z.object({ id: UuidSchema });

/** `GET /alerts/:id/context`: ancestors plus siblings (port of `contextRows`). */
export const AlertContextRowSchema = z.object({
  pn: z.string(),
  description: z.string(),
  depth: z.number().int().nonnegative(),
  pdmQty: z.number().nullable(),
  erpQty: z.number().nullable(),
  isThisAlert: z.boolean(),
  isAncestor: z.boolean(),
});
export const AlertContextResponseSchema = z.object({ rows: z.array(AlertContextRowSchema) });
export type AlertContextResponse = z.infer<typeof AlertContextResponseSchema>;

// Action bodies. Every one carries expectedVersion (BR-032).

const reason = (message: string) => z.string().trim().min(1, message);

export const AcceptRecommendationRequestSchema = ExpectedVersionSchema;
export const MarkFixedRequestSchema = ExpectedVersionSchema;
export const RecheckRequestSchema = ExpectedVersionSchema;
/** Prototype copy for the empty-reason error. */
export const AcceptDeviationRequestSchema = ExpectedVersionSchema.extend({
  reason: reason('Add a reason so your team knows why.'),
});
/** Only after a timeout, with a reason (spec §4, BR-011). */
export const CloseAlertRequestSchema = ExpectedVersionSchema.extend({
  reason: reason('Add a reason for closing this alert.'),
});
export const ReassignAlertRequestSchema = ExpectedVersionSchema.extend({
  userId: UuidSchema,
  note: z.string().trim().max(2000).optional(),
});
export const EscalateAlertRequestSchema = ExpectedVersionSchema.extend({
  note: z.string().trim().max(2000).optional(),
});

export const ALERT_ACTION_REQUEST_SCHEMAS = {
  'accept-recommendation': AcceptRecommendationRequestSchema,
  'mark-fixed': MarkFixedRequestSchema,
  'accept-deviation': AcceptDeviationRequestSchema,
  reassign: ReassignAlertRequestSchema,
  recheck: RecheckRequestSchema,
  close: CloseAlertRequestSchema,
  escalate: EscalateAlertRequestSchema,
} as const satisfies Record<z.infer<typeof AlertActionSchema>, z.ZodType>;

export const AlertActionResponseSchema = z.object({ alert: AlertDetailSchema });
export type AlertActionResponse = z.infer<typeof AlertActionResponseSchema>;

/** API only in the MVP (acceptance G). There is no bulk send (BR-029). */
export const BulkAcceptRequestSchema = z.object({
  items: z
    .array(z.object({ alertId: UuidSchema, expectedVersion: z.number().int().nonnegative() }))
    .min(1)
    .max(200),
});
export type BulkAcceptRequest = z.infer<typeof BulkAcceptRequestSchema>;

export const BulkAcceptResponseSchema = z.object({
  results: z.array(
    z.object({
      alertId: UuidSchema,
      ok: z.boolean(),
      errorCode: z.string().optional(),
      draftId: UuidSchema.nullable(),
    }),
  ),
});
export type BulkAcceptResponse = z.infer<typeof BulkAcceptResponseSchema>;
